import { useMemo } from "react";

import { formatMediaPlaybackTime } from "../MediaTimelineBar";
import type {
  AnnotationValidationIssueFrameLocation,
  AnnotationValidationIssueLocation,
} from "./AnnotationSetValidationAction";
import { hasAnnotationTargetContent } from "./annotationTargetContent";
import type {
  AnnotationFrameDraft,
  AnnotationFrameSummary,
  AnnotationSavedFrameIndexSnapshot,
  AnnotationSequence,
  AnnotationSequenceDirectorySnapshot,
  AnnotationSetDraft,
  AnnotationSetValidationIssue,
  AnnotationSetValidationState,
  AnnotationWorkspaceSnapshot,
} from "./types";

function countFramesByContext(
  frameIds: ReadonlySet<string>,
  frames: AnnotationSetDraft["frames"] | undefined,
  summaries?: ReadonlyMap<string, AnnotationFrameSummary>,
) {
  const byStream = new Map<string, number>();
  const bySequenceAndStream = new Map<string, Map<string, number>>();
  for (const frameId of frameIds) {
    const candidateFrame = frames?.[frameId] ?? summaries?.get(frameId);
    if (!candidateFrame) continue;
    byStream.set(candidateFrame.streamId, (byStream.get(candidateFrame.streamId) ?? 0) + 1);
    const streamCounts =
      bySequenceAndStream.get(candidateFrame.sequenceId) ?? new Map<string, number>();
    streamCounts.set(candidateFrame.streamId, (streamCounts.get(candidateFrame.streamId) ?? 0) + 1);
    bySequenceAndStream.set(candidateFrame.sequenceId, streamCounts);
  }
  return { byStream, bySequenceAndStream };
}

const frameAnnotationContentCache = new WeakMap<AnnotationFrameDraft, boolean>();

function frameHasAnnotationContent(frame: AnnotationFrameDraft): boolean {
  const cached = frameAnnotationContentCache.get(frame);
  if (cached !== undefined) return cached;
  const hasContent = Object.values(frame.targets).some(hasAnnotationTargetContent);
  frameAnnotationContentCache.set(frame, hasContent);
  return hasContent;
}

export function useAnnotationSetPresentation({
  workspace,
  draft,
  savedFrameIndex,
  sequenceDirectory,
  pinnedSequence,
  frame,
  validation,
  saveConflictFrameIds: unresolvedSaveConflictFrameIds,
}: {
  workspace: AnnotationWorkspaceSnapshot;
  draft: AnnotationSetDraft | null;
  savedFrameIndex?: AnnotationSavedFrameIndexSnapshot | null;
  sequenceDirectory?: AnnotationSequenceDirectorySnapshot | null;
  pinnedSequence?: AnnotationSequence | null;
  frame?: AnnotationFrameDraft;
  validation: AnnotationSetValidationState | null;
  saveConflictFrameIds: readonly string[];
}) {
  const draftFrames = draft?.frames;
  const orderedFrameIds = draft?.orderedFrameIds;
  const dirtyScopes = draft?.dirtyScopes;
  const orderedFrameIdSet = useMemo(() => new Set(orderedFrameIds ?? []), [orderedFrameIds]);
  const savedFrameSummaries = useMemo(
    () =>
      new Map(savedFrameIndex?.orderedFrames.map((summary) => [summary.frameId, summary]) ?? []),
    [savedFrameIndex],
  );
  const knownFrameIdSet = useMemo(
    () => new Set([...orderedFrameIdSet, ...savedFrameSummaries.keys()]),
    [orderedFrameIdSet, savedFrameSummaries],
  );
  const dirtyTargetIds = useMemo(() => {
    const next = new Set<string>();
    if (!frame) return next;
    for (const scope of dirtyScopes ?? []) {
      if (scope.kind === "target" && scope.frameId === frame.frameId) next.add(scope.targetId);
    }
    return next;
  }, [dirtyScopes, frame]);

  const dirtyFrameIds = useMemo(() => {
    const next = new Set<string>();
    for (const scope of dirtyScopes ?? []) {
      if (scope.kind === "target") next.add(scope.frameId);
    }
    return next;
  }, [dirtyScopes]);

  const dirtyFrameIdsByStream = useMemo(() => {
    const next = new Map<string, string[]>();
    for (const frameId of dirtyFrameIds) {
      const streamId = draftFrames?.[frameId]?.streamId;
      if (!streamId) continue;
      const streamFrameIds = next.get(streamId) ?? [];
      streamFrameIds.push(frameId);
      next.set(streamId, streamFrameIds);
    }
    return next;
  }, [dirtyFrameIds, draftFrames]);

  const dirtyFrameCounts = useMemo(
    () => countFramesByContext(dirtyFrameIds, draftFrames),
    [dirtyFrameIds, draftFrames],
  );

  const saveConflictFrameIds = useMemo(
    () =>
      new Set(
        unresolvedSaveConflictFrameIds.filter((frameId) => draftFrames?.[frameId] !== undefined),
      ),
    [draftFrames, unresolvedSaveConflictFrameIds],
  );
  const saveConflictFrameCounts = useMemo(
    () => countFramesByContext(saveConflictFrameIds, draftFrames),
    [draftFrames, saveConflictFrameIds],
  );

  const hasAnnotationContent = useMemo(() => {
    for (const summary of savedFrameSummaries.values()) {
      const localFrame = draftFrames?.[summary.frameId];
      if (
        localFrame ? frameHasAnnotationContent(localFrame) : summary.annotatedTargetIds.length > 0
      ) {
        return true;
      }
    }
    return Object.entries(draftFrames ?? {}).some(
      ([frameId, localFrame]) =>
        !savedFrameSummaries.has(frameId) && frameHasAnnotationContent(localFrame),
    );
  }, [draftFrames, savedFrameSummaries]);

  const sequenceLabels = useMemo(
    () =>
      new Map(
        [
          ...workspace.sequences,
          ...(sequenceDirectory?.items ?? []),
          ...(pinnedSequence ? [pinnedSequence] : []),
        ].map((sequence) => [sequence.id, sequence.label]),
      ),
    [pinnedSequence, sequenceDirectory?.items, workspace.sequences],
  );
  const streamLabels = useMemo(
    () => new Map(workspace.streams.map((stream) => [stream.id, stream.label])),
    [workspace.streams],
  );
  const validationReviewDestination = useMemo(() => {
    if (!validation || validation.status === "ready" || validation.status === "validating") {
      return null;
    }

    const issueDestination = (issue: AnnotationSetValidationIssue) => {
      const frameId = [issue.frameId, ...(issue.affectedFrameIds ?? [])].find(
        (candidate): candidate is string =>
          candidate !== undefined && knownFrameIdSet.has(candidate),
      );
      const locatedFrameId = [issue.location, ...(issue.affectedLocations ?? [])].find(
        (location) => location !== undefined && knownFrameIdSet.has(location.frameId),
      )?.frameId;
      const destinationFrameId = frameId ?? locatedFrameId;
      return destinationFrameId
        ? { frameId: destinationFrameId, targetId: issue.targetId ?? null }
        : null;
    };

    if (validation.status === "blocked") {
      for (const issue of validation.issues) {
        const destination = issueDestination(issue);
        if (destination) return destination;
      }
    }

    for (let index = (dirtyScopes?.length ?? 0) - 1; index >= 0; index -= 1) {
      const scope = dirtyScopes?.[index];
      if (scope?.kind === "target" && draftFrames?.[scope.frameId]) {
        return { frameId: scope.frameId, targetId: scope.targetId };
      }
    }

    for (const issue of validation.issues) {
      const destination = issueDestination(issue);
      if (destination) return destination;
    }
    return null;
  }, [dirtyScopes, draftFrames, knownFrameIdSet, validation]);

  const validationLocationFrameIds = useMemo(() => {
    const frameIds = new Set<string>();
    if (validationReviewDestination) frameIds.add(validationReviewDestination.frameId);
    for (const issue of validation?.issues ?? []) {
      for (const frameId of [
        issue.frameId,
        ...(issue.affectedFrameIds ?? []),
        issue.location?.frameId,
        ...(issue.affectedLocations ?? []).map((location) => location.frameId),
      ]) {
        if (frameId !== undefined && knownFrameIdSet.has(frameId)) {
          frameIds.add(frameId);
        }
      }
    }
    return frameIds;
  }, [knownFrameIdSet, validation?.issues, validationReviewDestination]);

  // Validation presentation only consumes the destination and issue-related frames. Building
  // locations for the entire draft made an unrelated point edit walk every captured frame.
  const frameLocations = useMemo(() => {
    const locations = new Map<string, AnnotationValidationIssueFrameLocation>();
    for (const frameId of validationLocationFrameIds) {
      const candidateFrame = draftFrames?.[frameId] ?? savedFrameSummaries.get(frameId);
      if (!candidateFrame) continue;
      const sequenceLabel =
        sequenceLabels.get(candidateFrame.sequenceId) ?? candidateFrame.sequenceId;
      const streamLabel = streamLabels.get(candidateFrame.streamId) ?? candidateFrame.streamId;
      const timestampLabel = formatMediaPlaybackTime(candidateFrame.timestampSeconds);
      locations.set(frameId, {
        frameId,
        timestampLabel,
        contextLabel: `${sequenceLabel} · ${streamLabel}`,
        path: `${sequenceLabel} / ${streamLabel} / ${timestampLabel}`,
        annotatedTargetIds: new Set(
          "targets" in candidateFrame
            ? Object.values(candidateFrame.targets)
                .filter(hasAnnotationTargetContent)
                .map((candidateTarget) => candidateTarget.targetId)
            : candidateFrame.annotatedTargetIds,
        ),
      });
    }
    return locations;
  }, [draftFrames, savedFrameSummaries, sequenceLabels, streamLabels, validationLocationFrameIds]);

  const validationSummaryLocation = validationReviewDestination
    ? (frameLocations.get(validationReviewDestination.frameId)?.path ?? null)
    : null;

  const validationIssueLocations = useMemo(() => {
    const locations = new Map<string, AnnotationValidationIssueLocation>();
    if (!validation) return locations;
    for (const issue of validation.issues) {
      const frameIds = [
        ...new Set([
          issue.frameId,
          ...(issue.affectedFrameIds ?? []),
          issue.location?.frameId,
          ...(issue.affectedLocations ?? []).map((location) => location.frameId),
        ]),
      ].filter(
        (frameId): frameId is string => frameId !== undefined && frameLocations.has(frameId),
      );
      const frames = frameIds.flatMap((frameId) => {
        const location = frameLocations.get(frameId);
        return location ? [location] : [];
      });
      if (frames.length > 0) locations.set(issue.id, { frames });
    }
    return locations;
  }, [frameLocations, validation]);

  const validationIssueFrameIds = useMemo(
    () =>
      new Set(
        validation?.status === "blocked" || validation?.status === "stale"
          ? validation.issues.flatMap((issue) => [
              ...(issue.affectedFrameIds ?? []),
              ...(issue.frameId ? [issue.frameId] : []),
              ...(issue.affectedLocations ?? []).map((location) => location.frameId),
              ...(issue.location ? [issue.location.frameId] : []),
            ])
          : [],
      ),
    [validation?.issues, validation?.status],
  );
  const validationIssueFrameCounts = useMemo(
    () => countFramesByContext(validationIssueFrameIds, draftFrames, savedFrameSummaries),
    [draftFrames, savedFrameSummaries, validationIssueFrameIds],
  );

  const savedAnnotationFrameIds = useMemo(() => {
    if (savedFrameIndex) {
      return new Set(
        savedFrameIndex.orderedFrames.flatMap((savedFrame) =>
          savedFrame.annotatedTargetIds.length > 0 ? [savedFrame.frameId] : [],
        ),
      );
    }
    const baseSetId = draft?.base?.setId;
    if (!baseSetId) return new Set<string>();
    const savedSet = workspace.annotationSets.find((set) => set.setId === baseSetId);
    return new Set(
      savedSet?.orderedFrames.flatMap((savedFrame) =>
        Object.values(savedFrame.targets).some(hasAnnotationTargetContent)
          ? [savedFrame.frameId]
          : [],
      ) ?? [],
    );
  }, [draft?.base?.setId, savedFrameIndex, workspace.annotationSets]);

  return {
    dirtyTargetIds,
    dirtyFrameIds,
    dirtyFrameIdsByStream,
    dirtyFrameCountsByStream: dirtyFrameCounts.byStream,
    dirtyFrameCountsBySequenceAndStream: dirtyFrameCounts.bySequenceAndStream,
    saveConflictFrameIds,
    saveConflictFrameCountsByStream: saveConflictFrameCounts.byStream,
    saveConflictFrameCountsBySequenceAndStream: saveConflictFrameCounts.bySequenceAndStream,
    hasUnsavedAnnotationChanges: dirtyFrameIds.size > 0,
    hasAnnotationContent,
    validationReviewDestination,
    validationSummaryLocation,
    validationIssueLocations,
    validationIssueFrameIds,
    validationIssueFrameCountsByStream: validationIssueFrameCounts.byStream,
    validationIssueFrameCountsBySequenceAndStream: validationIssueFrameCounts.bySequenceAndStream,
    savedAnnotationFrameIds,
  };
}
